// PX-Connect-Terminerfassung — gemeinsamer Zustand + Persistenz für beide Einstiege
// (eigenes Terminerfassungs-Modal und PXC-Pfad im Korrespondenz-Modal), siehe ADR 0005.
import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { usePermission } from '~/hooks';
import { useToast } from '@chakra-ui/react';
import { GetProjectDocument, UpdateProjectMetadataDocument, type Projekt } from '~/gql/ucpw/graphql';
import {
    PxConnectOrtstermin,
    validatePxConnectTermine,
} from '~/pages/projects/meta/data/pxConnect';
import {
    buildPxConnectTerminePersistedState,
    darfTermineSchreiben,
    hasPxConnectTermineChanges,
    mergePxConnectTermineMetadata,
    pxConnectTermineFromMetadata,
} from '~/pages/projects/meta/data/pxConnectTermine';
import type { PxConnectTermineRecord } from '~/pages/projects/meta/data/pxConnectTermine';
import { assertProjectMetadataSaved } from '~/meta/data/px-connect';
import log from '~/log';

type UsePxConnectTermineArgs = {
    projectId?: number;
    /** Nur solange true wird geladen/hydriert; beim Wechsel auf false wird zurückgesetzt. */
    active: boolean;
};

export const usePxConnectTermine = ({ projectId, active }: UsePxConnectTermineArgs) => {
    const toast = useToast();
    const context = { clientName: 'ucpw' };

    // PXC-B-23: Bis hierher hing das Recht ALLEIN am Knopf im Projektüberblick —
    // Dialog, Block und diese Funktion haben es nie geprüft. Wer den Knopf auf einem
    // anderen Weg erreichte, durfte schreiben. Das Recht gehört an die Handlung, nicht
    // an den Einstieg: dann darf der Knopf auch ohne Recht erscheinen, und „ansehen
    // darf jeder" (beide Vorgabeseiten) ist wieder erfüllt.
    const { canCreate: darfSchreiben, isLoading: rechtLaedt } = usePermission(
        'pxconnect.terminerfassung'
    );

    const { data, loading } = useQuery(GetProjectDocument, {
        variables: { id: projectId as number },
        context,
        skip: !active || !projectId,
        fetchPolicy: 'network-only',
    });
    const project = data?.item?.item as Projekt | undefined;

    const [updateProjectMetadata] = useMutation(UpdateProjectMetadataDocument, { context });
    const [values, setValues] = React.useState<Record<string, string>>({});
    const [ortstermine, setOrtstermine] = React.useState<PxConnectOrtstermin[]>([]);
    const [persistedRecord, setPersistedRecord] = React.useState<
        PxConnectTermineRecord | undefined
    >();
    const [saving, setSaving] = React.useState(false);

    // Einmal pro Aktivierung aus den Metadaten hydrieren. Nicht bei jedem Re-Render:
    // das eigene "Speichern" schreibt in denselben GetProject-Cache-Eintrag zurück,
    // und ein erneutes Hydrieren aus diesem Echo würde spätere Edits verwerfen
    // (gleiche Mechanik wie im PxConnectModal-Schadenbericht).
    const [hydrated, setHydrated] = React.useState(false);
    React.useEffect(() => {
        if (!active) {
            setHydrated(false);
            setValues({});
            setOrtstermine([]);
            setPersistedRecord(undefined);
        }
    }, [active]);

    React.useEffect(() => {
        if (!active || hydrated || loading || !project) return;
        const stored = pxConnectTermineFromMetadata(project.metadata);
        if (stored) {
            setValues(stored.values);
            setOrtstermine(stored.ortstermine);
            setPersistedRecord(stored);
        }
        setHydrated(true);
    }, [active, hydrated, loading, project]);

    const fieldErrors = validatePxConnectTermine(values);
    const hasErrors = Object.keys(fieldErrors).length > 0;
    // Jedes save() stempelt `savedAt` neu und löst über crud.event den Worker aus — wer nur
    // die Korrespondenz bearbeitet, soll den Datensatz nicht anfassen, wenn nichts anders ist.
    const hasChanges = hasPxConnectTermineChanges(persistedRecord, values, ortstermine);
    const persisted = React.useMemo(
        () => buildPxConnectTerminePersistedState(persistedRecord, values, ortstermine),
        [persistedRecord, values, ortstermine]
    );

    const save = async (): Promise<boolean> => {
        if (!projectId || !project || hasErrors) return false;
        if (!darfTermineSchreiben(rechtLaedt, darfSchreiben)) {
            toast({
                title: 'Keine Berechtigung für die Terminerfassung',
                status: 'error',
                position: 'top',
                duration: 5000,
                isClosable: true,
            });
            return false;
        }
        setSaving(true);
        try {
            const record = {
                values,
                ortstermine,
            };
            const result = await updateProjectMetadata({
                variables: {
                    projektId: projectId,
                    version: project.version,
                    forceOverwrite: true,
                    metadata: mergePxConnectTermineMetadata(project.metadata, record),
                },
            });
            assertProjectMetadataSaved(result);
            setPersistedRecord({ ...record, savedAt: new Date().toISOString() });
            toast({
                title: 'Terminerfassung gespeichert',
                status: 'success',
                position: 'top',
                duration: 3000,
                isClosable: true,
            });
            return true;
        } catch (err) {
            log.error('PxConnectTermine.save', err);
            toast({
                title: 'Terminerfassung konnte nicht gespeichert werden',
                description: (err as Error)?.message,
                status: 'error',
                position: 'top',
                duration: 5000,
                isClosable: true,
            });
            return false;
        } finally {
            setSaving(false);
        }
    };

    return {
        values,
        ortstermine,
        setValues,
        setOrtstermine,
        save,
        saving,
        persisted,
        hasErrors,
        hasChanges,
        loading: loading || !hydrated,
        ready: Boolean(project),
        /** Falsch nur, wenn das Recht geladen IST und fehlt — nie währenddessen. */
        darfSchreiben: darfTermineSchreiben(rechtLaedt, darfSchreiben),
    };
};
